"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Combobox, HStack, Portal, Text, useFilter, useListCollection } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { getCountryData } from "./country-data";
const DEFAULT_LABELS = {
    chooseCountry: "Choose country",
    searchCountry: "Search country…",
    openCountryList: "Open country list",
    noCountries: "No countries found",
};
export const CountrySelector = ({ value, onChange, disabled = false, size = "md", locale = "en", labels, portalled = true, portalContainer, }) => {
    const text = { ...DEFAULT_LABELS, ...labels };
    const countries = useMemo(() => getCountryData(locale), [locale]);
    const { contains } = useFilter({ sensitivity: "base" });
    const [isOpen, setIsOpen] = useState(false);
    const { collection, filter, set } = useListCollection({
        initialItems: countries,
        itemToString: (item) => `${item.label} ${item.dialCode} ${item.value}`,
        itemToValue: (item) => item.value,
        filter: contains,
    });
    const previousCountries = useRef(countries);
    useEffect(() => {
        if (previousCountries.current === countries)
            return;
        previousCountries.current = countries;
        set(countries);
    }, [countries, set]);
    const handleSelect = (details) => {
        const selected = countries.find((item) => item.value === details.itemValue);
        if (!selected)
            return;
        onChange(selected);
        setIsOpen(false);
        filter("");
    };
    const handleOpenChange = (details) => {
        setIsOpen(details.open);
        if (!details.open) {
            filter("");
        }
    };
    return (_jsxs(Combobox.Root, { collection: collection, onInputValueChange: (details) => {
            filter(details.inputValue);
        }, onSelect: handleSelect, onOpenChange: handleOpenChange, value: value ? [value.value] : [], open: isOpen, 
        // The list (one row and one flag per country) is built the first time it
        // opens, not with every phone field that is never opened.
        lazyMount: true, openOnClick: true, selectionBehavior: "clear", multiple: false, size: size, disabled: disabled, width: value ? "120px" : "140px", flexShrink: 0, positioning: { strategy: "fixed", hideWhenDetached: true }, children: [_jsxs(Combobox.Control, { borderEndRadius: 0, borderEndWidth: 0, children: [_jsx(Combobox.Input, { "aria-label": text.chooseCountry, placeholder: isOpen ? text.searchCountry : value?.dialCode ?? text.countryPlaceholder ?? text.chooseCountry, style: { textIndent: value && !isOpen ? "2rem" : undefined }, css: {
                            paddingEnd: "6 !important",
                            ...(value ? { "&::placeholder": { color: "var(--chakra-colors-fg)" } } : {}),
                        }, minW: 0, textOverflow: "ellipsis", borderEndWidth: 0, borderEndRadius: 0 }), value && !isOpen && (_jsx(Box, { position: "absolute", left: "3", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center", children: _jsx(ReactCountryFlag, { countryCode: value.value, svg: true, style: { width: "1.2em", height: "1.2em", display: "block" }, "aria-label": value.label }) })), _jsx(Combobox.IndicatorGroup, { children: _jsx(Combobox.Trigger, { "aria-label": text.openCountryList }) })] }), _jsx(Portal, { disabled: !portalled, container: portalContainer, children: _jsx(Combobox.Positioner, { style: { zIndex: 10001 }, children: _jsxs(Combobox.Content, { minW: "320px", maxH: "320px", overflowY: "auto", children: [_jsx(Combobox.Empty, { children: text.noCountries }), collection.items.map((item) => (_jsxs(Combobox.Item, { item: item, children: [_jsx(Combobox.ItemText, { children: _jsxs(HStack, { gap: 3, children: [_jsx(ReactCountryFlag, { countryCode: item.value, svg: true, style: { width: "1.25em", height: "1.25em" } }), _jsx(Text, { fontWeight: "medium", children: item.dialCode }), _jsx(Text, { children: item.label })] }) }), _jsx(Combobox.ItemIndicator, {})] }, item.value)))] }) }) })] }));
};
